import { useNavigate } from 'react-router-dom';
import type { MonitoringOverview } from '../../../services/dmMonitoringOverview';
import { formatShortDate } from '../../../utils/dates';
import { plural } from '../../../utils/plural';
import { HubStatCard } from '../../accounts/overview/HubStatCard';
import { formatAge, pickOldestItem } from './overviewTime';
import { handledSplitLabel, handledTotals } from './overviewHandled';
import { HANDLED_WHERE_EXPLAINER } from './handledWhereCopy';
import { KPI_ROW_CLASS } from './overviewLayout';
import { MESSAGES_LIST_PATH, NOTIFICATIONS_LIST_PATH } from './overviewLinks';
import type { HistoryView } from './overviewPeriod';

const NO_VALUE = '-';
const LOADING_SUB = 'Loading';
const LIVE_FAILED_SUB = "Couldn't load";
const HANDLED_FAILED_SUB = "Couldn't load this range";
const HANDLED_NONE_SUB = 'Nothing handled';
const HANDLED_INFO = `Notifications and messages resolved in this range. ${HANDLED_WHERE_EXPLAINER}`;

const OLDEST_KIND_LABEL = { notification: 'Notification', message: 'Message' } as const;
const LIVE_LABELS = {
    notificationPatients: 'Patients with notifications',
    messagePatients: 'Patients with messages',
    oldest: 'Oldest open item'
} as const;

function OldestItemCard({ overview }: { overview: MonitoringOverview }) {
    const navigate = useNavigate();
    const oldest = pickOldestItem(overview.age);
    if (!oldest)
        return <HubStatCard label={LIVE_LABELS.oldest} value={NO_VALUE} sub="Nothing open" />;
    const age = formatAge(oldest.at, Date.parse(overview.generatedAt)) ?? NO_VALUE;
    const path = oldest.category === 'notification' ? NOTIFICATIONS_LIST_PATH : MESSAGES_LIST_PATH;
    return (
        <HubStatCard
            label={LIVE_LABELS.oldest}
            value={age}
            sub={`${OLDEST_KIND_LABEL[oldest.category]} raised ${formatShortDate(oldest.at)}`}
            onClick={() => navigate(path)}
        />
    );
}

function HandledCard({ history }: { history: HistoryView }) {
    const label = `Handled · ${history.period.label}`;
    if (!history.data) {
        return (
            <HubStatCard
                label={label}
                info={HANDLED_INFO}
                value={NO_VALUE}
                tone="muted"
                sub={history.failed ? HANDLED_FAILED_SUB : LOADING_SUB}
            />
        );
    }
    const handled = handledTotals(history.data.resolved);
    return (
        <HubStatCard
            label={label}
            info={HANDLED_INFO}
            value={handled.total.toLocaleString()}
            sub={handledSplitLabel(handled) || HANDLED_NONE_SUB}
        />
    );
}

function LiveKpis({ overview }: { overview: MonitoringOverview }) {
    const navigate = useNavigate();
    const { open } = overview;
    return (
        <>
            <HubStatCard
                label={LIVE_LABELS.notificationPatients}
                value={open.notificationPatients.toLocaleString()}
                sub={plural(open.notifications, 'notification')}
                onClick={() => navigate(NOTIFICATIONS_LIST_PATH)}
            />
            <HubStatCard
                label={LIVE_LABELS.messagePatients}
                value={open.messagePatients.toLocaleString()}
                sub={plural(open.messages, 'message')}
                onClick={() => navigate(MESSAGES_LIST_PATH)}
            />
            <OldestItemCard overview={overview} />
        </>
    );
}

function LiveKpiPlaceholders({ failed }: { failed: boolean }) {
    return Object.values(LIVE_LABELS).map((label) => (
        <HubStatCard
            key={label}
            label={label}
            value={NO_VALUE}
            tone="muted"
            sub={failed ? LIVE_FAILED_SUB : LOADING_SUB}
        />
    ));
}

interface OverviewKpiRowProps {
    overview: MonitoringOverview | null;
    liveFailed: boolean;
    history: HistoryView;
}

export function OverviewKpiRow({ overview, liveFailed, history }: OverviewKpiRowProps) {
    return (
        <div className={KPI_ROW_CLASS}>
            {overview ? (
                <LiveKpis overview={overview} />
            ) : (
                <LiveKpiPlaceholders failed={liveFailed} />
            )}
            <HandledCard history={history} />
        </div>
    );
}
